import { useEffect, useState, type ReactNode } from "react";
import { NavLink, useLocation, useNavigate, Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CreditMeter, Icon, VocalerLogo, Dot } from "../lib/brand";
import { Dropdown, Sheet } from "../lib/ui";
import { MiniPlayer } from "../lib/player/MiniPlayer";
import { useResponsive } from "../hooks/useResponsive";
import { useTheme } from "./useTheme";
import {
  NAV_SECTIONS,
  ADMIN_SECTIONS,
  MOBILE_TABS,
  ADMIN_MOBILE_TABS,
  isAdminPath,
  type NavItem,
} from "./nav";
import { useLocale } from "../i18n/useLocale";
import { useAuth } from "../lib/auth/AuthContext";
import { useCredits } from "../lib/credits/CreditsContext";
import { services } from "../services";
import { NotificationBell } from "../features/notifications/NotificationBell";
import { ActiveJobsTray } from "../features/renders/ActiveJobsTray";
import { RemixFlowProvider, RemixTakeoverSlot } from "../features/generate/RemixFlowContext";
import type { Plan, User } from "../types";

const FALLBACK_USER: User = { id: "?", name: "—", initials: "·", email: "—" };
const FALLBACK_PLAN: Plan = { name: "—", renews: "—", creditsTotal: 0, creditsUsed: 0 };

export function AppShell() {
  const { isMobile } = useResponsive();
  const [navOpen, setNavOpen] = useState(false);

  return (
    <div
      style={{ minHeight: "100vh", background: "var(--bg)", color: "var(--fg)", display: "flex" }}
    >
      <a href="#main" className="voc-skip-link">
        Skip to content
      </a>
      {/* RemixFlowProvider owns the in-place remix takeover. Mounted at the
          shell level (peer of CreditsProvider over in App.tsx) so it can
          overlay the main content region — it renders the staged pipeline
          as a full-area overlay INSIDE <main> while the sidebar + topbar
          chrome stay put. No route change. */}
      <RemixFlowProvider>
        {!isMobile && <Sidebar />}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
          <TopBar onOpenNav={() => setNavOpen(true)} isMobile={isMobile} />
          {/* position:relative so the remix takeover overlay (inset:0,
              absolute) the provider renders covers exactly this content
              region — the topbar above and the sidebar beside it remain
              interactive. */}
          <main
            id="main"
            style={{
              flex: 1,
              minWidth: 0,
              position: "relative",
              paddingBottom: isMobile ? 64 : 0,
            }}
          >
            <Outlet />
            <RemixTakeoverSlot />
          </main>
        </div>
      </RemixFlowProvider>
      {isMobile && <BottomNav />}
      {isMobile && (
        <Sheet open={navOpen} onClose={() => setNavOpen(false)} title="Menu" side="bottom">
          <NavSections onNavigate={() => setNavOpen(false)} />
        </Sheet>
      )}
      {/* Persistent bottom mini-player. Auto-hides when no track is
          loaded; renders above the mobile BottomNav. */}
      <MiniPlayer />
    </div>
  );
}

function Sidebar() {
  const admin = isAdminPath(useLocation().pathname);
  return (
    <aside
      style={{
        width: 240,
        flexShrink: 0,
        height: "100vh",
        position: "sticky",
        top: 0,
        borderRight: "1px solid var(--border)",
        background: "var(--bg)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{ padding: "16px 16px 8px" }}>
        <VocalerLogo size={36} />
      </div>
      <nav aria-label="Primary" style={{ flex: 1, overflowY: "auto", padding: "8px 12px 12px" }}>
        <NavSections />
      </nav>
      {/* The plan card is a tenant-surface control and its "Upgrade" link
          points at /plan, i.e. back into studio. Admin does not get it. */}
      {!admin && <PlanCard />}
    </aside>
  );
}

function NavSections({ onNavigate }: { onNavigate?: () => void }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  // Which product you are in decides which sidebar you get. Studio never
  // lists admin and admin never lists studio.
  const admin = isAdminPath(useLocation().pathname);
  const [plan, setPlan] = useState<Plan>(FALLBACK_PLAN);
  useEffect(() => {
    let cancelled = false;
    services.account.plan().then((p) => { if (!cancelled) setPlan(p); }).catch(() => undefined);
    return () => { cancelled = true; };
  }, []);
  // Operator-only sections (e.g. the Platform group) only render when the
  // bearer's user row has is_operator=true. The OperatorGate on each route
  // is the security layer; this is the UX hint.
  const sections = (admin ? ADMIN_SECTIONS : NAV_SECTIONS).filter(
    (sec) => !sec.operatorOnly || user?.isOperator,
  );
  // Free + Pro tenants don't see Studio+-gated items (e.g. Integrations / BYOK).
  // Operators always see everything for management; Studio + Enterprise see
  // the gated items because their plan unlocks them.
  const isStudioPlus = (() => {
    const p = (plan.name || "").toLowerCase();
    return p === "studio" || p === "enterprise";
  })();
  const visibleForPlan = (it: NavItem) =>
    !it.studioPlanOnly || isStudioPlus || user?.isOperator;
  return (
    <>
      {sections.map((sec) => (
        <div key={sec.labelKey} style={{ marginBottom: 20 }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 500,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: "var(--fg-subtle)",
              padding: "6px 12px 8px",
            }}
          >
            {t(sec.labelKey as never)}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {sec.items.filter(visibleForPlan).map((it) => (
              <NavRow key={it.id} item={it} onNavigate={onNavigate} />
            ))}
          </div>
        </div>
      ))}
    </>
  );
}

/**
 * NavLink matches by prefix unless `end` is set, so any nav path that is a
 * parent of the others in its set would stay highlighted everywhere beneath
 * it. That is "/" for studio and "/admin" for admin.
 */
const isSectionRoot = (path: string) => path === "/" || path === "/admin";

function NavRow({ item, onNavigate }: { item: NavItem; onNavigate?: () => void }) {
  const { t } = useTranslation();
  return (
    <NavLink
      to={item.path}
      end={isSectionRoot(item.path)}
      onClick={onNavigate}
      style={({ isActive }) => ({
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "8px 12px",
        borderRadius: 6,
        background: isActive ? "var(--bg-elev)" : "transparent",
        color: isActive ? "var(--fg)" : "var(--fg-muted)",
        fontSize: 13,
        fontWeight: isActive ? 500 : 400,
        boxShadow: isActive ? "inset 2px 0 0 #1845FF" : "none",
      })}
    >
      <Icon name={item.icon} size={16} />
      {t(item.labelKey as never)}
      {item.stub && (
        <span
          style={{
            marginLeft: "auto",
            fontSize: 9,
            color: "var(--fg-subtle)",
            fontFamily: "Geist Mono, monospace",
          }}
        >
          soon
        </span>
      )}
    </NavLink>
  );
}

function PlanCard() {
  const { t } = useTranslation();
  const [plan, setPlan] = useState<Plan>(FALLBACK_PLAN);
  useEffect(() => {
    let cancelled = false;
    services.account
      .plan()
      .then((p) => {
        if (!cancelled) setPlan(p);
      })
      .catch(() => {
        /* leave fallback */
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return (
    <div style={{ padding: 16, borderTop: "1px solid var(--border)" }}>
      <div
        style={{
          background: "var(--bg-elev)",
          border: "1px solid var(--border)",
          borderRadius: 6,
          padding: 12,
        }}
      >
        <div
          style={{ fontSize: 11, color: "var(--fg-subtle)", fontFamily: "Geist Mono, monospace" }}
        >
          {t("shell.plan_label").toUpperCase()}
        </div>
        <div style={{ fontSize: 13, color: "var(--fg)", marginTop: 4 }}>{plan.name}</div>
        <div
          style={{
            fontSize: 11,
            color: "var(--fg-muted)",
            fontFamily: "Geist Mono, monospace",
            marginTop: 4,
          }}
        >
          {t("shell.renews", { date: plan.renews })}
        </div>
        <a
          href="/plan"
          style={{
            marginTop: 10,
            display: "block",
            fontSize: 12,
            color: "var(--accent-blue-text, #6B8AFF)",
          }}
        >
          {t("shell.upgrade")} →
        </a>
      </div>
    </div>
  );
}

function TopBar({ onOpenNav, isMobile }: { onOpenNav: () => void; isMobile: boolean }) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggle } = useTheme();
  const { user: liveUser, logout } = useAuth();
  const { balance: ledgerBalance, ledgerEnabled } = useCredits();
  const { code: localeCode, locales, setLocale } = useLocale();
  const [plan, setPlan] = useState<Plan>(FALLBACK_PLAN);
  const breadcrumb = location.pathname === "/" ? "home" : location.pathname.replace(/^\//, "");
  const user = liveUser ?? FALLBACK_USER;
  useEffect(() => {
    let cancelled = false;
    services.account
      .plan()
      .then((p) => {
        if (!cancelled) setPlan(p);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);
  // Prefer the live ledger balance when the credit ledger is enabled and
  // reported a value; otherwise fall back to the plan-derived remainder.
  // The ledger refreshes after each confirmed spend (see CreditsContext),
  // so the chip ticks down the moment a render is confirmed.
  const planCredits =
    Math.max(0, plan.creditsTotal - plan.creditsUsed) + (plan.bonusCredits ?? 0);
  const creditsRemaining =
    ledgerEnabled && ledgerBalance != null ? Math.max(0, Math.round(ledgerBalance)) : planCredits;
  async function onSignOut() {
    await logout();
    navigate("/login", { replace: true });
  }

  return (
    <header
      style={{
        position: "sticky",
        top: 0,
        zIndex: 5,
        height: 56,
        borderBottom: "1px solid var(--border)",
        background: "var(--app-topbar-bg, rgba(10,10,11,0.84))",
        backdropFilter: "blur(12px)",
        display: "flex",
        alignItems: "center",
        gap: 16,
        padding: isMobile ? "0 16px" : "0 32px",
      }}
    >
      {isMobile ? (
        <>
          <button aria-label={t("shell.open_menu")} onClick={onOpenNav} style={iconBtn}>
            <Icon name="menu" size={18} />
          </button>
          <VocalerLogo size={20} />
        </>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{ fontFamily: "Geist Mono, monospace", fontSize: 12, color: "var(--fg-subtle)" }}
          >
            /
          </span>
          <span
            style={{ fontFamily: "Geist Mono, monospace", fontSize: 12, color: "var(--fg-muted)" }}
          >
            {breadcrumb}
          </span>
        </div>
      )}

      <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>
        {!isMobile && <CommandTrigger />}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        {isMobile && (
          <button
            aria-label={t("actions.search")}
            style={iconBtn}
            onClick={() => window.dispatchEvent(new CustomEvent("voc:cmdk"))}
          >
            <Icon name="search" size={16} />
          </button>
        )}
        {!isMobile && <CreditMeter credits={creditsRemaining} />}
        {/* Background queue: shows every in-flight render regardless of which
            screen started it, so navigating away never hides what's cooking.
            Renders nothing when idle. Compact (icon + badge) on mobile. */}
        <ActiveJobsTray compact={isMobile} />
        {/* Single notification bell. The theme toggle and language picker now
            live inside the user dropdown below — the header bar previously
            duplicated both, plus a second static (non-functional) bell. */}
        <NotificationBell />
        <Dropdown
          align="end"
          trigger={({ toggle: t2, id }) => (
            <button
              aria-haspopup="menu"
              aria-controls={id}
              onClick={t2}
              style={{
                width: 32,
                height: 32,
                borderRadius: 9999,
                background: "#1f2330",
                border: "none",
                color: "#F2F0EC",
                fontSize: 12,
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              {user.initials}
            </button>
          )}
          items={[
            { label: user.email, onSelect: () => navigate("/settings") },
            { label: t("shell.profile"), onSelect: () => navigate("/settings") },
            { label: t("shell.settings"), onSelect: () => navigate("/settings") },
            { label: `${t("shell.theme")}: ${theme}`, onSelect: toggle },
            // Language lives here now (was a standalone header control) to
            // save space in the bar. One row per locale, ✓ on the active one.
            ...(locales.length > 1
              ? locales.map((l) => ({
                  label: `${l.code === localeCode ? "✓" : "🌐"} ${t("language.label")}: ${l.label}`,
                  onSelect: () => void setLocale(l.code),
                }))
              : []),
            { label: t("shell.sign_out"), onSelect: onSignOut, destructive: true },
          ]}
        />
      </div>
    </header>
  );
}

function CommandTrigger() {
  const { t } = useTranslation();
  return (
    <button
      onClick={() => window.dispatchEvent(new CustomEvent("voc:cmdk"))}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        height: 36,
        padding: "0 14px",
        borderRadius: 9999,
        border: "1px solid var(--border)",
        background: "var(--bg-elev)",
        width: 380,
        maxWidth: "100%",
        cursor: "pointer",
        color: "var(--fg-muted)",
      }}
    >
      <Icon name="search" size={14} />
      <span style={{ fontSize: 13 }}>{t("shell.search_placeholder")}</span>
      <span
        style={{
          marginLeft: "auto",
          fontFamily: "Geist Mono, monospace",
          fontSize: 11,
          color: "var(--fg-subtle)",
          border: "1px solid var(--border)",
          borderRadius: 4,
          padding: "1px 6px",
        }}
      >
        ⌘K
      </span>
    </button>
  );
}

function BottomNav() {
  const { t } = useTranslation();
  const tabs = isAdminPath(useLocation().pathname) ? ADMIN_MOBILE_TABS : MOBILE_TABS;
  return (
    <nav
      aria-label="Primary"
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        height: 64,
        zIndex: 30,
        display: "flex",
        background: "var(--app-topbar-bg, rgba(10,10,11,0.92))",
        backdropFilter: "blur(12px)",
        borderTop: "1px solid var(--border)",
      }}
    >
      {tabs.map((it) => (
        <NavLink
          key={it.id}
          to={it.path}
          end={isSectionRoot(it.path)}
          style={({ isActive }) => ({
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 4,
            color: isActive ? "var(--fg)" : "var(--fg-muted)",
            fontSize: 10,
          })}
        >
          <Icon name={it.icon} size={20} />
          {t(it.labelKey as never)}
        </NavLink>
      ))}
    </nav>
  );
}

const iconBtn: React.CSSProperties = {
  width: 32,
  height: 32,
  borderRadius: 9999,
  border: "1px solid var(--border)",
  background: "transparent",
  color: "var(--fg-muted)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
};

interface StubMeta {
  blurb: string;
  bullets: string[];
}

const STUB_META: Record<string, StubMeta> = {
  "Publish queue": {
    blurb:
      "When a render lands and you've connected a channel, Vocaler queues it for posting. Schedule, retry, and audit every upload from one feed.",
    bullets: [
      "Schedule posts for the right time per channel.",
      "Retry failed uploads with backoff and per-platform rate handling.",
      "Audit every publish with the response body Stripe/IG/TT returned.",
    ],
  },
  CLI: {
    blurb:
      "Drive Vocaler from your terminal — same endpoints, scriptable. Generate a track, render a batch, publish to channels — all from `vocaler …`.",
    bullets: [
      "`vocaler generate music --model suno-v5 --prompt …`",
      "`vocaler render album.json` runs the bundle pipeline.",
      "`vocaler publish` ships approved renders to connected channels.",
    ],
  },
  MCP: {
    blurb:
      "Vocaler as a Model Context Protocol server. Run music + image + video generation from Claude, Cursor, or any MCP-aware client.",
    bullets: [
      "Drop the studio into your editor as a tool surface.",
      "All your tenant's models become callable tools.",
      "Auth via tenant API key — no OAuth flow.",
    ],
  },
  Templates: {
    blurb:
      "Save a complete pipeline (music brief + style + image direction + video motion) as a named template, then reuse it across projects.",
    bullets: [
      "One-click new song from a saved template.",
      "Share templates across your team.",
      "Built-in templates for common genres.",
    ],
  },
  Plan: {
    blurb: "This page moved — see Settings → Billing.",
    bullets: [],
  },
};

export function StubPage({ name }: { name: string }): ReactNode {
  const { t } = useTranslation();
  const meta = STUB_META[name];
  return (
    <div style={{ maxWidth: 720, margin: "0 auto", padding: "80px 32px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
        <Dot state="queued" size={10} />
        <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--fg-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Coming soon
        </span>
      </div>
      <h1 style={{ fontSize: 32, fontWeight: 500, letterSpacing: "-0.02em" }}>{name}</h1>
      <p style={{ marginTop: 12, color: "var(--fg-muted)", fontSize: 15, lineHeight: 1.55 }}>
        {meta?.blurb ?? t("shell.stub_body")}
      </p>
      {meta && meta.bullets.length > 0 && (
        <ul style={{ marginTop: 18, padding: 0, listStyle: "none", display: "grid", gap: 8 }}>
          {meta.bullets.map((b, i) => (
            <li key={i} style={{ display: "flex", gap: 10, fontSize: 13, color: "var(--fg)" }}>
              <span style={{ color: "var(--fg-muted)", flexShrink: 0 }}>{String(i + 1).padStart(2, "0")}</span>
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
